import React, { useEffect, useState } from 'react';
import { FullUser, Package } from "../typings";
import { Row } from "../components/Flex";
import { useParams } from "react-router-dom";
import { PrimaryButton } from "../components/buttons/Buttons";
import { addPackage, fetchUser, getAvailablePackages, updateUser } from "./users-api";
import SelectField from "../components/SelectField";
import { useApi } from "../api-context";
import Switch from "react-switch";
import DatePicker from "react-datepicker";
import EditableInput from "../components/EditableInput";
import { toast } from "react-toastify";
import { set } from "react-datepicker/dist/date_utils";

function UserPage() {
    const api = useApi();
    const [user, setUser] = useState<FullUser>();
    const [availablePackages, setAvailablePackages] = useState<Package[]>([]);
    const [packageId, setPackageId] = useState<number>();

    const { id } = useParams();

    const loadUser = () => {
        if (!id) {
            return;
        }
        fetchUser(api, Number(id))
            .then(setUser)
            .catch(console.error);
    };

    const onUpdateUser = () => {
        if (!user) {
            return;
        }
        updateUser(api, Number(id), user)
            .then(() => toast.success('Saved changes'))
            .catch(() => toast.error('Failed to save changes'));
    };

    const onAddPackage = () => {
        if (!packageId) {
            return;
        }
        addPackage(api, Number(id), packageId)
            .then(() => {
                toast.success('Saved changes');
                setPackageId(undefined);
                loadUser();
            })
            .catch(() => toast.error('Failed to save changes'));
    };

    useEffect(() => {
        loadUser();
        getAvailablePackages(api).then(setAvailablePackages);
    }, []);

    if (!user) {
        return null;
    }

    return (
        <>
            <div className="group-page-header">
                <Row gap={8}>
                    <div style={{ flex: 1 }}>
                        <EditableInput
                            value={user?.email || ''}
                            setValue={email => {
                                setUser({ ...user, email });
                            }}
                        />
                    </div>
                    <PrimaryButton
                        label="SAVE"
                        style={{ flex: 0, minWidth: '150px' }}
                        onClick={onUpdateUser}
                    />
                </Row>
            </div>
            <div className="page-container">
                <div className="content-container" style={{ marginBottom: '32px' }}>
                    <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
                        <div className="box" style={{ flex: 3, gap: '12px' }}>
                            <SelectField
                                label="Permission"
                                placeholder="Select value"
                                value={user?.permission}
                                options={[
                                    { value: null, text: 'NO PERMISSION' },
                                    { value: 'CONTENT_MANAGEMENT', text: 'CONTENT MANAGEMENT'}
                                ]}
                                setValue={permission => setUser({...user, permission})}
                            />
                            <SelectField
                                label="Rights level"
                                placeholder="Select value"
                                value={user?.accountType}
                                options={[
                                    { value: 'PRO', text: 'PROFESSIONAL VERSION' },
                                    { value: 'PLAYER', text: 'PLAYER VERSION'}
                                ]}
                                setValue={accountType => setUser({...user, accountType})}
                            />
                            <div className="text-field">
                                <div className="label">Email confirmed</div>
                                <Switch onChange={emailConfirmed => setUser({...user, emailConfirmed})} checked={user.emailConfirmed} />
                            </div>
                            <div className="text-field">
                                <div className="label">Newsletter approved</div>
                                <Switch onChange={newsletterApproved => setUser({...user, newsletterApproved})} checked={user.newsletterApproved} />
                            </div>
                            <div className="text-field">
                                <div className="label">Expiry</div>
                                <Row gap={8}>
                                    <Switch
                                        onChange={ready => setUser({ ...user, expires: ready ? new Date().toISOString() : undefined })}
                                        checked={!!user?.expires} />
                                    <DatePicker
                                        disabled={!user.expires!}
                                        selected={user.expires ? new Date(user.expires) : undefined}
                                        onChange={date => {
                                            setUser({ ...user, expires: date?.toISOString() });
                                        }}
                                    />
                                </Row>
                            </div>
                            <div className="text-field">
                                <div className="label">Trial</div>
                                <Row gap={8}>
                                    <Switch
                                        onChange={ready => setUser({ ...user, trialExpires: ready ? new Date().toISOString() : undefined})}
                                        checked={!!user?.trialExpires} />
                                    <DatePicker
                                        disabled={!user.trialExpires!}
                                        selected={user.trialExpires ? new Date(user.trialExpires) : undefined}
                                        onChange={date => {
                                            setUser({ ...user, trialExpires: date?.toISOString() || undefined });
                                        }}
                                    />
                                </Row>
                            </div>
                        </div>
                        <div className="box" style={{ flex: 3, gap: '12px' }}>
                            <div className="text-field">
                                <div className="label">Starting password</div>
                                <div>{user?.startingPassword || '-'}</div>
                            </div>
                            <div className="text-field">
                                <div className="label">Purchase id</div>
                                <div>{user?.accountNumber || '-'}</div>
                            </div>
                            <div className="text-field">
                                <div className="label">Promo code</div>
                                <div>{user?.promoCode || '-'}</div>
                            </div>
                            <div className="text-field">
                                <div className="label">Last login</div>
                                <div>{user?.lastLogin}</div>
                            </div>
                            <div className="text-field">
                                <div className="label">Devices</div>
                                {user?.devices.map(device => (
                                    <div className="pill">{device}</div>
                                ))}
                            </div>
                        </div>
                        <div>
                            <div className="box" style={{ flex: 1, gap: '12px' }}>
                                <div className="label">Organisations</div>
                                {user?.portals.map(portal => (
                                    <div className="pill">{portal}</div>
                                ))}
                            </div>
                            <div className="box" style={{ flex: 1, gap: '12px' }}>
                                <div className="label">Packages</div>
                                {user?.packages.map(item => (
                                    <div className="pill">{item}</div>
                                ))}
                                <SelectField
                                    label="Select package"
                                    placeholder="Select value"
                                    value={packageId}
                                    options={availablePackages.map(p => ({
                                        value: p.id,
                                        text: p.name
                                    }))}
                                    setValue={setPackageId}
                                />
                                <PrimaryButton
                                    label="ADD PACKAGE"
                                    style={{ flex: 0, minWidth: '150px' }}
                                    onClick={onAddPackage}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default UserPage;
